// src/components/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, updateDoc } from 'firebase/firestore';
import Cookies from 'js-cookie';
import { db } from '../config/firebase';

// Component imports
import ContentCard from './ContentCard';
import ContentFilters from './ContentFilters';
import ThemeFilters from './ThemeFilters';
import ViewControls from './ViewControls';
import Logo from './Logo';
import AddContentForm from './AddContentForm';
import ContentModal from './ContentModal';
import MonthDivider from './MonthDivider';
import MobileHeader from './MobileHeader';

// Utils
import { contentTypeFilters, themeFilters } from '../utils/filters';

const Dashboard = () => {
  // =========== State Management ===========
  // Content state
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [editContent, setEditContent] = useState(null);
  const [username, setUsername] = useState(localStorage.getItem('contributorName') || '');

  // UI state
  const [showAddForm, setShowAddForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  // =========== Filter Initialization ===========
  const initialFilters = () => {
    try {
      const savedThemeFilters = Cookies.get('activeThemeFilters');
      const preference = Cookies.get('contentPreference');
      
      // If we have saved theme filters, use those
      if (savedThemeFilters) {
        return {
          contentType: contentTypeFilters.map(f => f.type),
          theme: JSON.parse(savedThemeFilters)
        };
      }
      
      // If we have a content preference but no saved filters
      if (preference) {
        switch (preference) {
          case 'development':
            return {
              contentType: contentTypeFilters.map(f => f.type),
              theme: ['Development']
            };
          case 'creatie':
            return {
              contentType: contentTypeFilters.map(f => f.type),
              theme: ['Creatie']
            };
          default:
            return {
              contentType: contentTypeFilters.map(f => f.type),
              theme: themeFilters.map(f => f.type)
            };
        }
      }
      
      // Default state: all filters selected
      return {
        contentType: contentTypeFilters.map(f => f.type),
        theme: themeFilters.map(f => f.type)
      };
    } catch (error) {
      console.error('Error initializing filters:', error);
      // Fallback to all filters selected if something goes wrong
      return {
        contentType: contentTypeFilters.map(f => f.type),
        theme: themeFilters.map(f => f.type)
      };
    }
  };

  // Filter state
  const [activeFilters, setActiveFilters] = useState(initialFilters());

  // =========== Effects ===========
  // Save filter preferences
  useEffect(() => {
    try {
      Cookies.set('activeThemeFilters', JSON.stringify(activeFilters.theme), { expires: 30 });
    } catch (error) {
      console.error('Error saving theme filters:', error);
    }
  }, [activeFilters.theme]);

  // Load initial content
  useEffect(() => {
    loadContent();
  }, []);

  // Keyboard events
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setShowAddForm(false);
        setSelectedItem(null);
        setEditContent(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // =========== Data Operations ===========
  const loadContent = async () => {
    try {
      const q = query(collection(db, 'content'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const contentItems = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setItems(contentItems);
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContentSubmit = async (contentData, isEdit) => {
    try {
      if (isEdit) {
        // Edit existing content
        const docRef = doc(db, 'content', contentData.id);
        const existingItem = items.find(item => item.id === contentData.id);
          
        if (!existingItem) {
          throw new Error('Item niet gevonden');
        }
  
        const updateData = {
          ...existingItem,
          ...contentData,
          updatedAt: new Date(),
          metadata: {
            ...(existingItem.metadata || {}),
            ...(contentData.metadata || {})
          }
        };
  
        // Clean up data before update
        delete updateData.createdAt;
        delete updateData.id;
  
        // Handle image data
        if (contentData.images?.length > 0) {
          updateData.images = contentData.images;
          delete updateData.image;
        } else if (existingItem.images?.length > 0) {
          updateData.images = existingItem.images;
          delete updateData.image;
        } else if (contentData.image) {
          updateData.image = contentData.image;
          delete updateData.images;
        } else if (existingItem.image) {
          updateData.image = existingItem.image;
          delete updateData.images;
        }
  
        // Remove undefined/null values
        Object.keys(updateData).forEach(key => {
          if (updateData[key] === undefined || updateData[key] === null) {
            delete updateData[key];
          }
        });
  
        await updateDoc(docRef, updateData);
        setItems(prevItems => prevItems.map(item => 
          item.id === contentData.id 
            ? { 
                ...updateData,
                id: item.id,
                createdAt: item.createdAt
              } 
            : item
        ));
      } else {
        // Add new content
        const { id, ...newContentData } = contentData;
        const newCreatedAt = new Date();
        
        const docRef = await addDoc(collection(db, 'content'), {
          ...newContentData,
          createdAt: newCreatedAt
        });
          
        const savedItem = {
          id: docRef.id,
          ...newContentData,
          createdAt: newCreatedAt
        };
          
        setItems([savedItem, ...items]);
      }
        
      setShowAddForm(false);
      setEditContent(null);
    } catch (error) {
      console.error('Error saving content:', error);
      alert(`Er is een fout opgetreden bij het opslaan: ${error.message}`);
    }
  };

  const handleDeleteContent = async (id) => {
    try {
      await deleteDoc(doc(db, 'content', id));
      setItems(prevItems => prevItems.filter(item => item.id !== id));
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  const handleEdit = (content) => {
    setEditContent(content);
    setShowAddForm(true);
    setSelectedItem(null);
  };

  // =========== Content Filtering & Grouping ===========
  const filteredItems = items.filter(item => {
    const contentTypeMatch = activeFilters.contentType.length === 0 || 
      activeFilters.contentType.includes(item.contentType);
    const themeMatch = activeFilters.theme.length === 0 || 
      activeFilters.theme.includes(item.theme);
    return contentTypeMatch && themeMatch;
  });

  const groupByMonth = (items) => {
    return items.reduce((groups, item) => {
      const date = new Date(item.createdAt?.toDate?.() || item.createdAt);
      const monthYear = new Intl.DateTimeFormat('nl-NL', { 
        month: 'long', 
        year: 'numeric' 
      }).format(date);
      
      if (!groups[monthYear]) {
        groups[monthYear] = [];
      }
      groups[monthYear].push(item);
      return groups;
    }, {});
  };

  // =========== Render Functions ===========
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (filteredItems.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400">
          <p className="text-lg mb-4">Geen content gevonden</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="text-blue-500 hover:text-blue-400 flex items-center"
          >
            <Plus size={20} className="mr-1" />
            Content toevoegen
          </button>
        </div>
      );
    }

    const groupedItems = groupByMonth(filteredItems);
    
    return (
      <div className="space-y-8">
        {Object.entries(groupedItems).map(([month, monthItems]) => (
          <div key={month} className="space-y-4">
            <MonthDivider month={month} isDarkMode={isDarkMode} />
            <div className={
              viewMode === 'grid'
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                : "space-y-4"
            }>
              {monthItems.map((item) => (
                <ContentCard 
                  key={item.id} 
                  item={item} 
                  contentTypeFilters={contentTypeFilters}
                  viewMode={viewMode}
                  isDarkMode={isDarkMode}
                  onClick={() => setSelectedItem(item)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // =========== Component Render ===========
  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#282828]' : 'bg-gray-100'}`}>
      {/* Mobile Header */}
      <div className="block md:hidden">
        <MobileHeader 
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          setShowAddForm={setShowAddForm}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
        />
      </div>

      <div className="flex flex-col md:flex-row">
        {/* Desktop Sidebar */}
        <div className="hidden md:flex md:w-72 flex-col">
          <Logo isDarkMode={isDarkMode} />
          <ContentFilters
            isOpen={showFilters}
            onClose={() => setShowFilters(false)}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            contentTypeFilters={contentTypeFilters}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header Controls - Desktop */}
            <div className="hidden md:flex justify-between items-center mb-6">
              {/* Theme Filters */}
              <div className="flex items-center gap-2">
                <ThemeFilters
                  activeFilters={activeFilters}
                  setActiveFilters={setActiveFilters}
                  themeFilters={themeFilters}
                  isDarkMode={isDarkMode}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <ViewControls
                  isDarkMode={isDarkMode}
                  setIsDarkMode={setIsDarkMode}
                  viewMode={viewMode}
                  setViewMode={setViewMode}
                />
                <button
                  onClick={() => setShowAddForm(true)}
                  className="h-10 bg-blue-600 text-white px-4 rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <Plus size={20} className="mr-2" />
                  Nieuwe content
                </button>
              </div>
            </div>

            {/* Content Grid */}
            {renderContent()}
          </div>
        </div>
      </div>

      {/* Modals */}
      {(showAddForm || selectedItem) && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
          onClick={() => {
            setShowAddForm(false);
            setSelectedItem(null);
            setEditContent(null);
          }}
        >
          {showAddForm && (
            <div className="max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
              <AddContentForm
                onSubmit={handleContentSubmit}
                onClose={() => {
                  setShowAddForm(false);
                  setEditContent(null);
                }}
                contentTypeFilters={contentTypeFilters}
                themeFilters={themeFilters}
                isDarkMode={isDarkMode}
                username={username}
                editContent={editContent}
              />
            </div>
          )}

          {selectedItem && (
            <div className="w-full" onClick={e => e.stopPropagation()}>
              <ContentModal
                content={selectedItem}
                isOpen={!!selectedItem}
                onClose={() => setSelectedItem(null)}
                onDelete={handleDeleteContent}
                onEdit={handleEdit}
                isDarkMode={isDarkMode}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;